import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { StrawberryTable } from '@dealstack/frontend/shared/components/Table/StrawberryTable/StrawberryTable';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useContext } from 'react';
import styled from 'styled-components';
import { getMainAddress } from '@dealstack/frontend/shared/address.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';
import { CustomDetailDataTypeEnum, CustomDetailEnum } from '@dealstack/shared/lib/generated';
import { useQuery } from '@tanstack/react-query';
import { getPersonBankAccounts } from '@dealstack/frontend/app/services/Person.service.ts';
import { getIdentifierRows } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData.ts';
import { UserInitialsBadge } from '@dealstack/frontend/shared/components/UserInitialsBadge/UserInitialsBadge';
import { Flex } from '@mantine/core';

export const PIVDetailsSection = () => {
	const manageInvestorDetailsController = useContext(ManageInvestorDetailsContext);
	const { investorDetailsData } = manageInvestorDetailsController;
	const tMipManage = useTranslationMipManage();
	const tCommonUI = useTranslationCommonUi();

	const pivPersonId = investorDetailsData?.pivPerson?.id;

	const pivCustomDetails = (investorDetailsData?.mipCustomDetails ?? []).filter(
		(cd) =>
			cd.dataType !== CustomDetailDataTypeEnum.BANK_ACCOUNT &&
			cd.dataType !== CustomDetailDataTypeEnum.FORM &&
			cd.dataType !== CustomDetailDataTypeEnum.PRE_FILLABLE_FORM &&
			cd.isChecked &&
			cd.type === CustomDetailEnum.VEHICLE
	);

	const pivDetailsHeaderArray = [
		tMipManage('investors.expanded_row.piv_name'),
		tMipManage('investors.expanded_row.piv_reg_number'),
		tMipManage('investors.expanded_row.address'),
		...pivCustomDetails.map((cd) => cd.name),
	];
	const { data: pivBankAccounts } = useQuery({
		queryKey: ['bankAccounts', pivPersonId],
		queryFn: () => (pivPersonId ? getPersonBankAccounts(pivPersonId) : null),
		enabled: !!pivPersonId,
	});

	if (!investorDetailsData) {
		return null;
	}

	const pivMainAddress = getMainAddress(investorDetailsData.pivPerson?.addresses);

	const pivDetailsBodyArray = [
		investorDetailsData.pivPerson?.displayName ?? { element: <>&mdash;</> },
		investorDetailsData.pivPerson?.registrationDetails?.corporateRegistrationNumber ?? {
			element: <>&mdash;</>,
		},
		pivMainAddress?.singleAddressString ?? { element: <>&mdash;</> },
		...pivCustomDetails.map((cd) => {
			return (
				investorDetailsData?.investorCustomDetails.find(
					(icd) => icd.customDetailId === cd.id
				)?.value || LONG_DASH
			);
		}),
	];

	const firstPivBankAccount = pivBankAccounts?.[0];
	const bankAccountHeaderArray = [
		tMipManage('investors.expanded_row.account_holder_name'),
		tMipManage('investors.expanded_row.bank_name'),
		tMipManage('investors.expanded_row.account_details', { defaultValue: 'Account details' }),
		tMipManage('investors.expanded_row.country', { defaultValue: 'Country' }),
		tMipManage('investors.expanded_row.currency', { defaultValue: 'Currency' }),
	];
	const pivIdentifierRows = getIdentifierRows(firstPivBankAccount?.identifiers);
	const bankAccountBodyArray = [
		firstPivBankAccount?.accountHolder ?? { element: <>&mdash;</> },
		firstPivBankAccount?.bankName ?? { element: <>&mdash;</> },
		pivIdentifierRows.length
			? {
					element: (
						<div className={REPLAY_MASK_CLASS} data-sentry-mask>
							{pivIdentifierRows.map((row) => (
								<div key={row.key}>{`${row.label}: ${row.value}`}</div>
							))}
						</div>
					),
					textToCopy: pivIdentifierRows.map((row) => row.value).join(' '),
				}
			: { element: <>&mdash;</> },
		(firstPivBankAccount?.countryCode
			? getCountryNameByCode(firstPivBankAccount.countryCode)
			: '') ||
			firstPivBankAccount?.countryCode || { element: <>&mdash;</> },
		firstPivBankAccount?.currency ?? { element: <>&mdash;</> },
	];

	const legalRepresentativeHeaderArray = investorDetailsData.representativePerson
		? [
				<Flex align="center" gap="1rem" key={0}>
					<UserInitialsBadge
						firstName={investorDetailsData.representativePerson.firstName ?? ''}
						lastName={investorDetailsData.representativePerson.lastName ?? ''}
						textVariant="xs"
					/>
					{investorDetailsData.representativePerson.displayName}
				</Flex>,
			]
		: [<>&mdash;</>];
	const legalRepresentativeBodyArray = [
		{
			element: (
				<div style={{ marginLeft: '40px' }}>
					{investorDetailsData.representativePerson?.email}
				</div>
			),
			textToCopy: investorDetailsData.representativePerson?.email ?? undefined,
		},
	];

	return (
		<div style={{ position: 'relative' }}>
			<PIVTextLabel disableVariantMapping variant="sans/17/800">
				{tMipManage('investors.expanded_row.piv_title_details')}
				{!investorDetailsData.pivPerson && (
					<ChipsV2 color={COLOR.PEACH} chipTheme={CHIP_THEME.LIGHT} outline size="s">
						{tCommonUI('chips.not_provided')}
					</ChipsV2>
				)}
			</PIVTextLabel>
			{investorDetailsData.pivPerson && (
				<PIVDetailsWrapper>
					<UIElementIdentifier
						identifier={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_PIV_TABLE}
					>
						<PIVDetailsBlock>
							<Text disableVariantMapping variant="sans/11/800">
								{tMipManage('investors.expanded_row.piv_details')}
							</Text>
							<StrawberryTable
								header={pivDetailsHeaderArray}
								body={pivDetailsBodyArray}
								hasUpperHorizontalLine
								hasBottomHorizontalLine
							/>
						</PIVDetailsBlock>
					</UIElementIdentifier>
					<BankDetailsBlock>
						<Text disableVariantMapping variant="sans/11/800">
							{tMipManage('investors.expanded_row.bank_account_details')}
						</Text>
						<StrawberryTable
							header={bankAccountHeaderArray}
							body={bankAccountBodyArray}
							hasUpperHorizontalLine
							hasBottomHorizontalLine
						/>
					</BankDetailsBlock>
					<UIElementIdentifier
						identifier={
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_PIV_REPRESENTATIVE_TABLE
						}
					>
						<LegalRepresentativeBlock>
							<Text disableVariantMapping variant="sans/11/800">
								{tMipManage('investors.expanded_row.legal_representative')}
							</Text>
							<StrawberryTable
								header={legalRepresentativeHeaderArray}
								body={legalRepresentativeBodyArray}
								hasUpperHorizontalLine
								hideFirstHeaderHorizontalLine
							/>
						</LegalRepresentativeBlock>
					</UIElementIdentifier>
				</PIVDetailsWrapper>
			)}
		</div>
	);
};

const PIVDetailsWrapper = styled.div`
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	padding: 1rem;
	${StrawberryTable} {
		padding-left: 0;
	}
`;
const PIVTextLabel = styled(Text)`
	display: flex;
	flex-direction: row;
	gap: 1rem;
	margin-bottom: 1rem;
`;

const BankDetailsBlock = styled.div`
	margin-bottom: 2rem;
	> ${Text} {
		margin-bottom: 0.5rem;
	}
`;
const PIVDetailsBlock = styled.div`
	margin-bottom: 2rem;
	> ${Text} {
		margin-bottom: 0.5rem;
	}
`;
const LegalRepresentativeBlock = styled.div`
	${StrawberryTable} {
		td {
			width: fit-content;
		}
		${Text} {
			word-break: unset;
			width: fit-content;
		}
	}
	> ${Text} {
		margin-bottom: 0.5rem;
	}
`;
